import { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router";
import { Button, Row, Col, Typography, Space, Spin, Alert } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { FirebaseContext } from "../context/FirebaseContext";
import { ProjectService } from "../services/ProjectService";
import Workspace from "./Workspace";

const { Title, Text } = Typography;

const ProjectWorkspace: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const firebase = useContext(FirebaseContext);
  const user = firebase.auth.currentUser;

  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        setError("ID de proyecto no válido");
        setLoading(false);
        return;
      }

      if (!user) {
        setError("Debes estar autenticado para acceder al proyecto");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Crear el servicio directamente aquí para evitar dependencias problemáticas
        const projectService = new ProjectService(firebase.db, user);
        const projectData = await projectService.getProject(projectId);

        if (projectData) {
          setProject(projectData);
          setError(null);
        } else {
          setError(
            "Proyecto no encontrado o no tienes permisos para acceder a él"
          );
        }
      } catch (err) {
        console.error("Error loading project:", err);
        setError("Error al cargar el proyecto");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, user, firebase.db]); // Dependencias estables

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Spin size="large" tip="Cargando proyecto..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Link to="/">
              <Button type="primary">Volver a proyectos</Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: "24px" }}>
        <Alert
          message="Proyecto no encontrado"
          description="El proyecto que buscas no existe o no tienes permisos para acceder a él."
          type="warning"
          showIcon
          action={
            <Link to="/">
              <Button type="primary">Volver a proyectos</Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ height: "100%" }}>
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #d9d9d9",
          background: "#fafafa",
        }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space direction="vertical" size="small">
              <Space>
                <Link to="/">
                  <Button type="text" icon={<ArrowLeftOutlined />}>
                    Volver a proyectos
                  </Button>
                </Link>
              </Space>
              <Title level={4} style={{ margin: 0 }}>
                {project.name}
              </Title>
              <Text type="secondary">{project.description}</Text>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Última modificación: {project.lastModified.toLocaleDateString()}
              </Text>
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ padding: "24px" }}>
        <Workspace />
      </div>
    </div>
  );
};

export default ProjectWorkspace;
